// src/components/Cards/TeamCoachingModal.tsx
import { useState } from "react";

interface TeamCoachingModalProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  generating?: boolean;
  error: string | null;
  data: any | null;
  onGenerate?: () => void;
}

type TabKey = "overview" | "weakAreas" | "reps" | "actions";

const TABS: { key: TabKey; label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "weakAreas", label: "Weak Areas" },
  { key: "reps", label: "Reps" },
  { key: "actions", label: "Actions & Training" },
];

function urgencyStyle(urgency?: string) {
  const u = (urgency || "").toLowerCase();
  if (u === "high")
    return { color: "#F87171", bg: "rgba(248,113,113,0.15)", border: "rgba(248,113,113,0.3)" };
  if (u === "medium")
    return { color: "#FACC15", bg: "rgba(250,204,21,0.15)", border: "rgba(250,204,21,0.3)" };
  return { color: "#4ADE80", bg: "rgba(74,222,128,0.15)", border: "rgba(74,222,128,0.3)" };
}

function scoreColor(score: number | null | undefined) {
  if (score == null) return "rgba(255,255,255,0.35)";
  if (score >= 70) return "#4ADE80";
  if (score >= 50) return "#FACC15";
  return "#F87171";
}

function fmtArea(area?: string) {
  if (!area) return "—";
  return area.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function fmtDateTime(iso?: string) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function TeamCoachingModal({
  open,
  onClose,
  loading,
  generating,
  error,
  data,
  onGenerate,
}: TeamCoachingModalProps) {
  const [tab, setTab] = useState<TabKey>("overview");

  if (!open) return null;

  const totalReps: number = data?.totalReps ?? 0;
  const insightsReady: number = data?.insightsReady ?? 0;
  const insightsPending: number =
    data?.insightsPending ?? Math.max(0, totalReps - insightsReady);
  const hasEnoughData: boolean = !!data?.hasEnoughData;
  const minimumRequired: number = data?.minimumRequired ?? 2;
  const hasInsights = !!(data?.teamInsightsStatus === "done" || data?.teamQuickWin || data?.teamWeakAreas?.length || data?.teamInsights);

  const teamWeakAreas: any[] = data?.teamWeakAreas || [];
  const managerActionItems: any[] = data?.managerActionItems || [];
  const repSummaries: any[] = data?.repSummaries || [];
  const repsNeedingAttention: any[] = data?.repsNeedingAttention || [];
  const groupTrainingOpportunities: any[] = data?.groupTrainingOpportunities || [];

  const sectionCard: React.CSSProperties = {
    background: "linear-gradient(135deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 18,
    padding: "20px 22px",
    boxShadow: "0 8px 24px -6px rgba(0,0,0,0.4)",
    backdropFilter: "blur(12px)",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "#B8F890",
    fontWeight: 700,
    marginBottom: 10,
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.88)", backdropFilter: "blur(16px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{
          borderRadius: 24,
          background: "linear-gradient(160deg, #11101d 0%, #0d0c18 60%, #090912 100%)",
          border: "1px solid rgba(124,112,232,0.25)",
          boxShadow: "0 40px 100px -10px rgba(0,0,0,0.9), 0 0 50px rgba(124,112,232,0.12)",
        }}
      >
        {/* Subtle Ambient Top Glow */}
        <div
          className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: "#7C70E8" }}
        />

        {/* Header */}
        <div
          className="flex items-center justify-between relative z-10"
          style={{ padding: "22px 28px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 14,
                flexShrink: 0,
                background: "linear-gradient(135deg, rgba(124,112,232,0.2), rgba(184,248,144,0.1))",
                border: "1px solid rgba(124,112,232,0.35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 16px rgba(124,112,232,0.2)",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#B8F890" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="3" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </div>
            <div>
              <p style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.14em", color: "#B8F890", margin: "0 0 3px", fontWeight: 700 }}>
                AI Intelligence Center
              </p>
              <p style={{ fontSize: 18, fontWeight: 800, color: "#fff", margin: 0 }}>
                Team Coaching Overview
              </p>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {!loading && !generating && hasInsights && onGenerate && (
              <button
                onClick={onGenerate}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 18px",
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, rgba(124,112,232,0.25), rgba(124,112,232,0.12))",
                  color: "#fff",
                  border: "1px solid rgba(124,112,232,0.4)",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
                </svg>
                Regenerate
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                cursor: "pointer",
                color: "#fff",
                transition: "all 0.2s ease",
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        {!loading && !generating && hasInsights && (
          <div
            style={{
              display: "flex",
              gap: 8,
              padding: "0 28px",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              flexShrink: 0,
              position: "relative",
              zIndex: 10,
            }}
          >
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  padding: "14px 20px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: tab === t.key ? "linear-gradient(180deg, transparent 0%, rgba(124,112,232,0.12) 100%)" : "transparent",
                  border: "none",
                  color: tab === t.key ? "#fff" : "rgba(255,255,255,0.45)",
                  borderBottom: tab === t.key ? "2px solid #7C70E8" : "2px solid transparent",
                  marginBottom: -1,
                  transition: "all 0.2s ease",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ padding: "22px 26px" }}>
          {loading && (
            <div className="flex flex-col items-center justify-center gap-4" style={{ padding: "70px 20px" }}>
              <div className="w-11 h-11 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              <p style={{ fontSize: 15, color: "rgba(255,255,255,0.7)" }}>Loading team overview insights…</p>
            </div>
          )}

          {generating && (
            <div className="flex flex-col items-center justify-center gap-4" style={{ padding: "70px 20px" }}>
              <div className="w-11 h-11 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              <p style={{ fontSize: 15, fontWeight: 600, color: "#fff" }}>Generating team AI insights. Please wait…</p>
            </div>
          )}

          {!loading && !generating && error && (
            <div
              style={{
                borderRadius: 14,
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(255,255,255,0.05)",
                padding: "16px 20px",
                fontSize: 14,
                color: "#fff",
              }}
            >
              {error}
            </div>
          )}

          {!loading && !generating && !error && data && (
            <>
              {/* State 1: Not enough reps */}
              {!hasEnoughData && (
                <div
                  style={{
                    ...sectionCard,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    padding: "32px 24px",
                  }}
                >
                  <p style={{ fontSize: 16, color: "#fff", margin: 0, fontWeight: 600 }}>
                    Not enough rep data yet
                  </p>
                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", margin: 0 }}>
                    {data?.message ||
                      `At least ${minimumRequired} reps must have insights generated before team-wide AI insights can be created.`}{" "}
                    Currently {insightsReady} of {totalReps} ready.
                  </p>
                  <button
                    disabled
                    style={{
                      margin: "8px auto 0",
                      padding: "10px 22px",
                      borderRadius: 999,
                      fontSize: 14,
                      fontWeight: 700,
                      background: "rgba(255,255,255,0.04)",
                      color: "rgba(255,255,255,0.3)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      cursor: "not-allowed",
                    }}
                  >
                    Generate Team Insights
                  </button>
                </div>
              )}

              {/* State 2: Enough reps, insights not generated */}
              {hasEnoughData && !hasInsights && (
                <div
                  style={{
                    ...sectionCard,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    padding: "32px 24px",
                  }}
                >
                  <p style={{ fontSize: 16, color: "#fff", margin: 0, fontWeight: 600 }}>
                    Ready to generate team insights
                  </p>
                  <p style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", margin: 0, maxWidth: 480, marginLeft: "auto", marginRight: "auto" }}>
                    You have enough rep data to aggregate. Generate deep trends, systemic issues, and manager priorities for the whole team.
                  </p>
                  {onGenerate && (
                    <button
                      onClick={onGenerate}
                      style={{
                        margin: "8px auto 0",
                        padding: "11px 26px",
                        borderRadius: 999,
                        fontSize: 14,
                        fontWeight: 700,
                        background: "#fff",
                        color: "#000",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Generate Team Insights
                    </button>
                  )}
                </div>
              )}

              {/* State 3: Insights available — tabbed content */}
              {hasEnoughData && hasInsights && (
                <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                  {/* ── OVERVIEW TAB ── */}
                  {tab === "overview" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div style={sectionCard}>
                          <p style={labelStyle}>Total Reps</p>
                          <p style={{ fontSize: 28, fontWeight: 800, color: "#fff", margin: 0 }}>{totalReps}</p>
                        </div>
                        <div style={sectionCard}>
                          <p style={labelStyle}>Insights Ready</p>
                          <p style={{ fontSize: 28, fontWeight: 800, color: "#fff", margin: 0 }}>{insightsReady}</p>
                        </div>
                        <div style={sectionCard}>
                          <p style={labelStyle}>Pending</p>
                          <p style={{ fontSize: 28, fontWeight: 800, color: "rgba(255,255,255,0.65)", margin: 0 }}>{insightsPending}</p>
                        </div>
                        <div style={sectionCard}>
                          <p style={labelStyle}>Last Updated</p>
                          <p style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 }}>
                            {fmtDateTime(data?.teamInsightsGeneratedAt)}
                          </p>
                        </div>
                      </div>

                 

                      {(data?.teamQuickWin || data?.teamInsights?.teamQuickWin) && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>⚡ Team Quick Win</p>
                          <p style={{ fontSize: 16, color: "#fff", margin: 0, lineHeight: 1.6 }}>
                            {data?.teamInsights?.teamQuickWin || data?.teamQuickWin}
                          </p>
                          {data?.teamQuickWin &&
                            data?.teamInsights?.teamQuickWin &&
                            data.teamQuickWin !== data.teamInsights.teamQuickWin && (
                              <p style={{ fontSize: 16, color: "rgba(255,255,255,0.55)", margin: "10px 0 0", lineHeight: 1.6 }}>
                                {data.teamQuickWin}
                              </p>
                            )}
                        </div>
                      )}

                      {data?.teamInsights?.teamAssessment && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>🤖 Team Assessment</p>
                          <p style={{ fontSize: 16, color: "#fff", margin: 0, lineHeight: 1.6 }}>
                            {data.teamInsights.teamAssessment}
                          </p>
                        </div>
                      )}

                      {repsNeedingAttention.length > 0 && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>Reps Needing Attention</p>
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {repsNeedingAttention.map((r: any, i: number) => {
                              const u = urgencyStyle(r.urgency);
                              return (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 12,
                                    padding: "10px 14px",
                                    borderRadius: 10,
                                    background: "rgba(255,255,255,0.02)",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{r.name}</span>
                                    <span style={{ fontSize: 14, color: "rgba(255,255,255,0.55)" }}>
                                      Weak area: {fmtArea(r.topWeakArea)}
                                    </span>
                                  </div>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <span style={{ fontSize: 15, fontWeight: 700, color: scoreColor(r.score) }}>
                                      {r.score}%
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: "3px 10px",
                                        borderRadius: 999,
                                        background: u.bg,
                                        color: u.color,
                                        border: `1px solid ${u.border}`,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                      }}
                                    >
                                      {r.urgency || "—"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── WEAK AREAS TAB ── */}
                  {tab === "weakAreas" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {teamWeakAreas.length > 0 ? (
                        teamWeakAreas.map((w: any, i: number) => (
                          <div key={i} style={sectionCard}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                              <span style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>{fmtArea(w.area)}</span>
                              {w.isSystemic && (
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    padding: "3px 10px",
                                    borderRadius: 999,
                                    background: "rgba(255,255,255,0.1)",
                                    color: "#fff",
                                    border: "1px solid rgba(255,255,255,0.25)",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                  }}
                                >
                                  Systemic
                                </span>
                              )}
                            </div>
                            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", margin: "0 0 12px" }}>
                              Affects {w.affectedReps} rep{w.affectedReps !== 1 ? "s" : ""} ({w.affectedPercentage})
                            </p>
                            {Array.isArray(w.repScores) && w.repScores.length > 0 && (
                              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                {w.repScores.map((rs: any, idx: number) => (
                                  <div
                                    key={idx}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      fontSize: 14,
                                    }}
                                  >
                                    <span style={{ color: "rgba(255,255,255,0.8)" }}>{rs.name}</span>
                                    <span style={{ fontWeight: 700, color: scoreColor(rs.score) }}>{rs.score}%</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }}>No weak areas identified.</p>
                      )}
                    </div>
                  )}

                  {/* ── REPS TAB ── */}
                  {tab === "reps" && (
                    <div style={sectionCard}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1.4fr 0.8fr 0.7fr 1fr 1.2fr",
                          gap: 12,
                          padding: "0 4px 12px",
                          borderBottom: "1px solid rgba(255,255,255,0.08)",
                          marginBottom: 6,
                        }}
                      >
                        {["Rep", "Role", "Score", "Weak Area", "Status"].map((h) => (
                          <span
                            key={h}
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.08em",
                              color: "rgba(255,255,255,0.45)",
                            }}
                          >
                            {h}
                          </span>
                        ))}
                      </div>
                      {repSummaries.length > 0 ? (
                        repSummaries.map((r: any) => (
                          <div
                            key={r.userId}
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1.4fr 0.8fr 0.7fr 1fr 1.2fr",
                              gap: 12,
                              padding: "12px 4px",
                              borderBottom: "1px solid rgba(255,255,255,0.04)",
                              alignItems: "center",
                            }}
                          >
                            <span style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{r.name}</span>
                            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>{r.role}</span>
                            <span style={{ fontSize: 15, fontWeight: 700, color: scoreColor(r.avgScore) }}>
                              {r.avgScore != null ? `${r.avgScore}%` : "—"}
                            </span>
                            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.6)" }}>
                              {fmtArea(r.topWeakArea)}
                            </span>
                            <span
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                width: "fit-content",
                                padding: "3px 10px",
                                borderRadius: 999,
                                background: r.insightsReady ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.04)",
                                color: r.insightsReady ? "#fff" : "rgba(255,255,255,0.4)",
                                border: `1px solid ${r.insightsReady ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.08)"}`,
                              }}
                            >
                              {r.insightsReady ? "Insights Ready" : "Pending"}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }}>No rep data available.</p>
                      )}
                    </div>
                  )}

                  {/* ── ACTIONS & TRAINING TAB ── */}
                  {tab === "actions" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                      {managerActionItems.length > 0 && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>Manager Action Items</p>
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {managerActionItems.map((a: any, i: number) => {
                              const u = urgencyStyle(a.urgency);
                              return (
                                <div
                                  key={i}
                                  style={{
                                    padding: "12px 16px",
                                    borderRadius: 12,
                                    background: "rgba(255,255,255,0.02)",
                                    border: "1px solid rgba(255,255,255,0.06)",
                                  }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                                    <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{a.repName}</span>
                                    <span
                                      style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        padding: "2px 9px",
                                        borderRadius: 999,
                                        background: u.bg,
                                        color: u.color,
                                        border: `1px solid ${u.border}`,
                                        textTransform: "uppercase",
                                        letterSpacing: "0.06em",
                                      }}
                                    >
                                      {a.urgency}
                                    </span>
                                  </div>
                                  <p style={{ fontSize: 16, color: "rgba(255,255,255,0.65)", margin: "0 0 6px" }}>
                                    Focus area: <span style={{ color: "#fff", fontWeight: 600 }}>{fmtArea(a.focusArea)}</span>
                                  </p>
                                  {a.action && (
                                    <p style={{ fontSize: 16, color: "rgba(255,255,255,0.8)", margin: "0 0 8px", lineHeight: 1.5 }}>
                                      {a.action}
                                    </p>
                                  )}
                                  {a.recommendedScenario && (
                                    <span
                                      style={{
                                        fontSize: 14,
                                        fontWeight: 600,
                                        padding: "3px 10px",
                                        borderRadius: 999,
                                        background: "rgba(255,255,255,0.05)",
                                        color: "rgba(255,255,255,0.7)",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                        display: "inline-block",
                                      }}
                                    >
                                      🎯 {a.recommendedScenario}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {Array.isArray(data?.teamInsights?.systemicIssues) && data.teamInsights.systemicIssues.length > 0 && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>AI Systemic Issues</p>
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            {data.teamInsights.systemicIssues.map((issue: any, i: number) => (
                              <div
                                key={i}
                                style={{
                                  padding: "14px 16px",
                                  borderRadius: 12,
                                  background: "rgba(255,255,255,0.02)",
                                  border: "1px solid rgba(255,255,255,0.06)",
                                }}
                              >
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                                  <span style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>{fmtArea(issue.area)}</span>
                                  <span
                                    style={{
                                      fontSize: 12,
                                      fontWeight: 700,
                                      padding: "3px 10px",
                                      borderRadius: 999,
                                      background: "rgba(255,255,255,0.08)",
                                      color: "rgba(255,255,255,0.7)",
                                      border: "1px solid rgba(255,255,255,0.18)",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.06em",
                                    }}
                                  >
                                    Systemic
                                  </span>
                                </div>
                                <p style={{ fontSize: 16, color: "#fff", margin: "0 0 8px", lineHeight: 1.5 }}>
                                  <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 600 }}>Team Action: </span>
                                  {issue.teamAction}
                                </p>
                                {Array.isArray(issue.affectedReps) && issue.affectedReps.length > 0 && (
                                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                    {issue.affectedReps.map((name: string, idx: number) => (
                                      <span
                                        key={idx}
                                        style={{
                                          fontSize: 14,
                                          fontWeight: 600,
                                          padding: "3px 10px",
                                          borderRadius: 999,
                                          background: "rgba(255,255,255,0.05)",
                                          color: "rgba(255,255,255,0.75)",
                                          border: "1px solid rgba(255,255,255,0.12)",
                                        }}
                                      >
                                        {name}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {Array.isArray(data?.teamInsights?.managerPriorities) && data.teamInsights.managerPriorities.length > 0 && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>AI Manager Priorities</p>
                          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                            {data.teamInsights.managerPriorities.map((priority: string, idx: number) => {
                              const parts = priority.split(":");
                              const repName = parts[0]?.trim();
                              const action = parts.slice(1).join(":").trim();
                              return (
                                <li key={idx} style={{ display: "flex", gap: 10, fontSize: 16, lineHeight: 1.6 }}>
                                  <span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 700 }}>•</span>
                                  <span style={{ color: "#fff" }}>
                                    {repName && action ? (
                                      <>
                                        <span style={{ fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>{repName}:</span> {action}
                                      </>
                                    ) : (
                                      priority
                                    )}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {groupTrainingOpportunities.length > 0 && (
                        <div style={sectionCard}>
                          <p style={labelStyle}>Group Training Opportunities</p>
                          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {groupTrainingOpportunities.map((g: any, i: number) => (
                              <div
                                key={i}
                                style={{
                                  padding: "14px 16px",
                                  borderRadius: 12,
                                  background: "rgba(255,255,255,0.02)",
                                  border: "1px solid rgba(255,255,255,0.06)",
                                }}
                              >
                                <p style={{ fontSize: 16, fontWeight: 700, color: "#fff", margin: "0 0 6px" }}>
                                  {g.scenario}
                                </p>
                                <p style={{ fontSize: 16, color: "#fff", margin: "0 0 8px" }}>
                                  {g.reason}
                                </p>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                  {(g.recommendedFor || []).map((name: string, idx: number) => (
                                    <span
                                      key={idx}
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        padding: "3px 10px",
                                        borderRadius: 999,
                                        background: "rgba(255,255,255,0.06)",
                                        color: "#fff",
                                        border: "1px solid rgba(255,255,255,0.15)",
                                      }}
                                    >
                                      {name}
                                    </span>
                                  ))}
                                  {g.targetArea && (
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        padding: "3px 10px",
                                        borderRadius: 999,
                                        background: "transparent",
                                        color: "rgba(255,255,255,0.5)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                      }}
                                    >
                                      Target: {fmtArea(g.targetArea)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {managerActionItems.length === 0 &&
                        groupTrainingOpportunities.length === 0 &&
                        !(data?.teamInsights?.systemicIssues?.length > 0) &&
                        !(data?.teamInsights?.managerPriorities?.length > 0) && (
                          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }}>
                            No action items or training opportunities yet.
                          </p>
                        )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 26px",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.12em", color: "rgba(255,255,255,0.35)", fontWeight: 600 }}>
            Team coaching powered by Pitchster
          </span>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff",
              padding: "8px 22px",
              borderRadius: 999,
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}